import { IS_TAURI } from './browser/globalEnvironment';
import { createCallbackManager } from './callbacks';
import { ESTABLISH_BROADCAST_CHANNEL_NAME } from './multiaccount';
import { getPasscodeHash, setPasscodeHash } from './passcode';

import Deferred from './Deferred';

const ESTABLISH_TIMEOUT = 100;

const { addCallback, runCallbacks } = createCallbackManager();
const { addCallback: addCallbackTokenDied, runCallbacks: runCallbacksTokenDied } = createCallbackManager();
const token = Number(Math.random().toString().substring(2));
const collectedTokens = new Set([token]);

let channel: BroadcastChannel | undefined;
let isEstablished = false;
const initialEstablishment = new Deferred();
let masterToken: number | undefined;
let isWaitingForMaster = false;
let reestablishToken: number | undefined;
let isChannelClosed = true;
let isListenerAttached = false;

type EstablishMessage = {
  collectedTokens: Set<number>;
  masterToken?: number;
  tokenDied?: number;
  currentPasscodeHash?: ArrayBuffer;
  reestablishToken?: number;
  shouldGiveUpMaster?: boolean;
  hasGaveUpMaster?: boolean;
};

function promoteSelfToMaster() {
  masterToken = token;
  isWaitingForMaster = false;
  if (!isEstablished) {
    isEstablished = true;
    initialEstablishment.resolve();
  }
  runCallbacks(true);
}

function getEstablishChannel(): BroadcastChannel | undefined {
  if (isChannelClosed || !channel) {
    try {
      channel = new BroadcastChannel(ESTABLISH_BROADCAST_CHANNEL_NAME);
      isChannelClosed = false;
      if (!isListenerAttached) {
        channel.addEventListener('message', handleMessage);
        isListenerAttached = true;
      }
    } catch {
      isChannelClosed = true;
      channel = undefined;
      return undefined;
    }
  }
  return channel;
}

function safeChannelPost(message: EstablishMessage): boolean {
  const activeChannel = getEstablishChannel();
  if (!activeChannel) return false;

  try {
    activeChannel.postMessage(message);
    return true;
  } catch {
    isChannelClosed = true;
    channel = undefined;
    return false;
  }
}

const handleMessage = ({ data }: { data: EstablishMessage }) => {
  if (!data) return;

  if (data.currentPasscodeHash) {
    setPasscodeHash(data.currentPasscodeHash);
  }

  if (data.hasGaveUpMaster && isWaitingForMaster) {
    promoteSelfToMaster();
    return;
  }

  if (data.shouldGiveUpMaster) {
    if (masterToken === token) {
      runCallbacks(false);
      safeChannelPost({ currentPasscodeHash: getPasscodeHash(), hasGaveUpMaster: true });
    }
    masterToken = data.masterToken;
    return;
  }

  if (data.tokenDied) {
    runCallbacksTokenDied(data.tokenDied);
    collectedTokens.delete(data.tokenDied);
    if (data.tokenDied === masterToken) {
      collectedTokens.delete(data.tokenDied);
      masterToken = undefined;
      isEstablished = false;
      reestablishToken = data.tokenDied;

      safeChannelPost({
        collectedTokens,
        masterToken,
        reestablishToken,
      });
      if (collectedTokens.size === 1) {
        isEstablished = true;
        masterToken = token;
        reestablishToken = undefined;
        initialEstablishment.resolve();
        runCallbacks(true);
      }
    }
  }

  if (data.collectedTokens) {
    if (!data.reestablishToken && reestablishToken) {
      return;
    }
    if (data.reestablishToken && reestablishToken !== data.reestablishToken) {
      data.collectedTokens.delete(data.reestablishToken);

      reestablishToken = data.reestablishToken;
    }
    const prevLength = collectedTokens.size;
    data.collectedTokens.forEach((l) => collectedTokens.add(l));
    if (reestablishToken) data.collectedTokens.delete(reestablishToken);

    if (!isEstablished) {
      if (data.masterToken) {
        reestablishToken = undefined;
        masterToken = data.masterToken;
        runCallbacks(masterToken === token);

        if (!isEstablished) {
          safeChannelPost({
            collectedTokens,
            masterToken,
            reestablishToken,
          });
        }
        initialEstablishment.resolve();
        isEstablished = true;
      } else if (prevLength !== collectedTokens.size) {
        safeChannelPost({
          collectedTokens,
          masterToken,
          reestablishToken,
        });
      } else {
        reestablishToken = undefined;
        masterToken = Math.max(...Array.from(collectedTokens));
        runCallbacks(masterToken === token);

        if (!isEstablished) {
          safeChannelPost({
            collectedTokens,
            masterToken,
            reestablishToken,
          });
        }
        initialEstablishment.resolve();
        isEstablished = true;
      }
    } else if (!data.masterToken) {
      safeChannelPost({
        collectedTokens,
        masterToken,
        reestablishToken,
      });
    }
  }
};

export function establishMultitabRole(shouldReestablishMasterToSelf?: boolean) {
  const activeChannel = getEstablishChannel();
  if (!activeChannel) {
    promoteSelfToMaster();
    return;
  }

  safeChannelPost({ collectedTokens });

  setTimeout(() => {
    if (masterToken === undefined) {
      promoteSelfToMaster();
    } else if (shouldReestablishMasterToSelf) {
      reestablishMasterToSelf();
    }
  }, ESTABLISH_TIMEOUT);

  window.addEventListener('beforeunload', signalTokenDead);
  if (IS_TAURI) window.addEventListener('unload', signalTokenDead);
}

export function signalTokenDead() {
  if (isChannelClosed || !channel) return;
  runCallbacksTokenDied(token);
  channel.removeEventListener('message', handleMessage);
  isListenerAttached = false;
  try {
    channel.postMessage({ tokenDied: token, currentPasscodeHash: getPasscodeHash() });
  } catch {
    // ignore
  }
  channel.close();
  channel = undefined;
  isChannelClosed = true;
}

export function signalPasscodeHash() {
  safeChannelPost({ currentPasscodeHash: getPasscodeHash() });
}

export function getCurrentTabId() {
  return token;
}

export function getAllMultitabTokens() {
  return Array.from(collectedTokens);
}

export function reestablishMasterToSelf() {
  isWaitingForMaster = true;
  const posted = safeChannelPost({
    collectedTokens, masterToken: token, shouldGiveUpMaster: true,
  });
  if (!posted) {
    promoteSelfToMaster();
  }
}

export const subscribeToTokenDied = addCallbackTokenDied;
export const subscribeToMasterChange = addCallback;

export const initialEstablishmentPromise = initialEstablishment.promise;

export function isCurrentTabMaster() {
  return masterToken === token;
}
